/*!
 * @Instruduction: AI 批量媒体存在性排查服务
 * 文档参考：前端接入指南 §4.3 / §4.4 / §4.5
 * 路径前缀：/api/v1/media/ai/batch-check
 */

import xhr from './xhr/index'
import { ICommonResponse } from './index'

/** 基础路径（http.ts 已自动添加 /v1 前缀） */
const BASE = '/media/ai/batch-check'

/** 批量排查任务状态枚举（与后端 RUNNING/COMPLETED/STOPPED/FAILED 对齐） */
export enum BatchCheckStatus {
    RUNNING = 'RUNNING',
    COMPLETED = 'COMPLETED',
    STOPPED = 'STOPPED',
    FAILED = 'FAILED'
}

/** 进度响应中的单条任务信息（后端字段已是下划线命名，下游也是下划线） */
export interface IBatchCheckTask {
    taskId: string
    total: number
    pending: number
    success: number
    failed: number
    skipped: number
    failedDetails: string[]
    stopFlag: boolean
    startTime: number
    status: BatchCheckStatus | string
    elapsedSec: number
    etaSec: number
}

/** 启动接口的入参 */
export interface IStartBatchCheckParams {
    /** 单次 AI 询问的信源数量上限（建议 4~16，-1 表示一次查全部） */
    batchSize?: number
    /** 本次任务最大排查媒体数（-1 表示不限，>0 用于测试） */
    maxMedia?: number
}

/**
 * 把对象序列化为 URL 查询字符串（含首部 ?）。
 * 设计目标：xhr 的 POST 内部把 params 视为 body，
 *          因此查询参数用此函数拼到 url 上以满足文档约定。
 */
function toQueryString(params: Record<string, any>): string {
    const parts: string[] = []
    Object.keys(params).forEach(k => {
        const v = params[k]
        if (v === undefined || v === null) return
        parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    })
    return parts.length > 0 ? `?${parts.join('&')}` : ''
}

class AiBatchCheckService {
    /**
     * 启动批量排查
     * POST /api/v1/media/ai/batch-check/start?batchSize=8&maxMedia=-1
     */
    public async startBatchCheck(params: IStartBatchCheckParams = {}): Promise<ICommonResponse<IBatchCheckTask>> {
        const defaults = { batchSize: -1, maxMedia: -1 }
        const merged = { ...defaults, ...params }
        const qs = toQueryString(merged)
        return await xhr({
            method: 'post',
            url: `${BASE}/start${qs}`,
            params: null
        })
    }

    /**
     * 查询进度（前端每 2s 轮询）
     * GET /api/v1/media/ai/batch-check/progress
     * 返回 data 为 null 表示当前没有任务
     */
    public async getProgress(): Promise<ICommonResponse<IBatchCheckTask | null>> {
        return await xhr({
            method: 'get',
            url: `${BASE}/progress`,
            params: {}
        })
    }

    /**
     * 停止排查任务
     * POST /api/v1/media/ai/batch-check/stop
     */
    public async stopBatchCheck(): Promise<ICommonResponse<any>> {
        return await xhr({
            method: 'post',
            url: `${BASE}/stop`,
            params: null
        })
    }
}

export default new AiBatchCheckService()
